import { useState, useMemo, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel
  } from '@mui/material';
  import { useDateSort } from '../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../components/common/TableActionsMenu';
  import AdpopcornMediationDeltaTop5 from '../avatar/AdpopcornMediationDeltaTop5';
  import { AdpopcornMediationRevenueMetrics } from '../../../../types/adpopcorn.types';
  import type { Dayjs } from 'dayjs';
  
  type Props = {
    data: AdpopcornMediationRevenueMetrics;
    selectedApp?: string | null;
    startDate: Dayjs;
    endDate: Dayjs;
  };
  
  export default function AdpopcornTotalMetricsTable({ data, selectedApp, startDate, endDate }: Props) {
    const top5MediationStats = data.top5MediationStats;
    const bottom5MediationStats = data.bottom5MediationStats;
    const mediationRevenueStats = data.mediationRevenueStats;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    const { sorted: sortedData, sortDirection, toggleSort } = useDateSort(mediationRevenueStats);

    // 파일명 생성
    const filename = useMemo(() => {
      const appPart = selectedApp && selectedApp !== '앱 전체' ? `_${selectedApp}` : '_전체';
      return `${startDate.format('YYMMDD')}-${endDate.format('YYMMDD')}${appPart}`;
    }, [startDate, endDate, selectedApp]);

    const paged = useMemo(
        () => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page]
    );

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    };
  // 엑셀용: 통화기호 없이 숫자만
  const formatNumberOnly = (value: number) => Math.round(value).toLocaleString('ko-KR');

    const formatRevenue = (value: number | undefined | null): string => {
        if (value === undefined || value === null || value === 0) {
            return '-';
        }
        return formatCurrency(value);
    };

    // 합계 계산 (모든 미디에이션별 합계)
    const totals = useMemo(() => {
        return {
            totalAdFit: sortedData.reduce((sum, row) => sum + (row.AdFit ?? 0), 0),
            totalADforus: sortedData.reduce((sum, row) => sum + (row.ADforus ?? 0), 0),
            totalAdMob: sortedData.reduce((sum, row) => sum + (row.AdMob ?? 0), 0),
            totalAdopRewardBanner: sortedData.reduce((sum, row) => sum + (row.AdopRewardBanner ?? 0), 0),
            totalAdPopcorn: sortedData.reduce((sum, row) => sum + (row.AdPopcorn ?? 0), 0),
            totalAppLovinBidding: sortedData.reduce((sum, row) => sum + (row.AppLovinBidding ?? 0), 0),
            totalAppLovinWaterfall: sortedData.reduce((sum, row) => sum + (row.AppLovinWaterfall ?? 0), 0),
            totalCauly: sortedData.reduce((sum, row) => sum + (row.Cauly ?? 0), 0),
            totalFyber: sortedData.reduce((sum, row) => sum + (row.Fyber ?? 0), 0),
            totalGAM: sortedData.reduce((sum, row) => sum + (row.GAM ?? 0), 0),
            totalMezzomedia: sortedData.reduce((sum, row) => sum + (row.Mezzomedia ?? 0), 0),
            totalMobon: sortedData.reduce((sum, row) => sum + (row.Mobon ?? 0), 0),
            totalMobWith: sortedData.reduce((sum, row) => sum + (row.MobWith ?? 0), 0),
            totalPangle: sortedData.reduce((sum, row) => sum + (row.Pangle ?? 0), 0),
            totalUnity: sortedData.reduce((sum, row) => sum + (row.Unity ?? 0), 0),
            totalVungle: sortedData.reduce((sum, row) => sum + (row.Vungle ?? 0), 0)
        };
    }, [sortedData]);

    // 엑셀 다운로드용 전체 데이터 (현재 정렬 상태 그대로, 페이지네이션 무시)
    const allRowsDataForExcel = useMemo(() => {
        return sortedData.map((row) => {
            return [
                row.date,
        formatNumberOnly(row.AdFit ?? 0),
        formatNumberOnly(row.ADforus ?? 0),
        formatNumberOnly(row.AdMob ?? 0),
        formatNumberOnly(row.AdopRewardBanner ?? 0),
        formatNumberOnly(row.AdPopcorn ?? 0),
        formatNumberOnly(row.AppLovinBidding ?? 0),
        formatNumberOnly(row.AppLovinWaterfall ?? 0),
        formatNumberOnly(row.Cauly ?? 0),
        formatNumberOnly(row.Fyber ?? 0),
        formatNumberOnly(row.GAM ?? 0),
        formatNumberOnly(row.Mezzomedia ?? 0),
        formatNumberOnly(row.Mobon ?? 0),
        formatNumberOnly(row.MobWith ?? 0),
        formatNumberOnly(row.Pangle ?? 0),
        formatNumberOnly(row.Unity ?? 0),
        formatNumberOnly(row.Vungle ?? 0)
            ];
        });
    }, [sortedData]);

    return (
        <Box>
            <AdpopcornMediationDeltaTop5
                top5MediationStats={top5MediationStats}
                bottom5MediationStats={bottom5MediationStats}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 1 }}>
                <TableActionsMenu 
                  tableRef={tableRef} 
                  allRowsData={allRowsDataForExcel}
                  filename={filename}
                  sheetName="미디에이션매출"
                />
            </Box>
            <TableContainer
                sx={{
                    overflowX: 'auto',
                    mt: 2
                }}
            >
              <Table ref={tableRef}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={toggleSort}
                      >
                        날짜
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>AdFit</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>ADforus</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>AdMob</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>ADOP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>AdPopcorn</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>AppLovin(Bidding)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>AppLovin(Waterfall)</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Cauly</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Fyber</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>GAM</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Mezzomedia</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Mobon</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>MobWith</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Pangle</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Unity</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 115 }}>Vungle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {/* 합계 행 */}
                    <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>합계</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAdFit)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalADforus)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAdMob)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAdopRewardBanner)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAdPopcorn)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAppLovinBidding)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalAppLovinWaterfall)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalCauly)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalFyber)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalGAM)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalMezzomedia)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalMobon)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalMobWith)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalPangle)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalUnity)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(totals.totalVungle)}
                        </TableCell>
                    </TableRow>
                    {paged.map((row) => {
                        return (
                            <TableRow key={row.date}>
                                <TableCell>{row.date}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AdFit)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.ADforus)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AdMob)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AdopRewardBanner)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AdPopcorn)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AppLovinBidding)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.AppLovinWaterfall)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Cauly)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Fyber)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.GAM)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Mezzomedia)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Mobon)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.MobWith)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Pangle)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Unity)}</TableCell>
                                <TableCell align="right">{formatRevenue(row.Vungle)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[rowsPerPage]}
            />
        </Box>
    );
  }
  